import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ArrowDownAZ, ArrowUpAZ, SlidersHorizontal, X } from 'lucide-react';
import {
  CONTRACT_STATUS_LABELS,
  RECEIPT_STATE_LABELS,
  SERVICE_ORDER_STATUS_LABELS,
} from '@/lib/documentsReport';
import {
  activeFilterCount,
  activeFiltersForTab,
  clearFiltersForTab,
  documentStatusToken,
  REPORT_FILTER_APPLICABILITY,
  type ReportFilterState,
  type ReportTab,
} from '@/lib/reportFilters';
import { QUOTE_STATUS_OPTIONS } from '@/lib/quotesReport';
import { SOLD_QUOTE_STATUSES } from '@/lib/itemsReport';
import type {
  ReportClientOption,
  ReportProductOption,
} from '@/hooks/useReportDimensions';
import { reportProductLabel } from '@/hooks/useReportDimensions';

const ALL = '__all__';

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Pago' },
  { value: 'overdue', label: 'Vencido' },
  { value: 'canceled', label: 'Cancelado' },
];

export interface ReportSortOption {
  value: string;
  label: string;
}

interface ReportFiltersSheetProps {
  tab: ReportTab;
  filters: ReportFilterState;
  onFiltersChange: (filters: ReportFilterState) => void;
  clients: ReportClientOption[];
  products: ReportProductOption[];
  sortOptions: readonly ReportSortOption[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSortFieldChange: (field: string) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
}

function statusLabel(tab: ReportTab, value: string | null): string | null {
  if (!value) return null;
  if (tab === 'documents') {
    const [type, status] = value.split(':');
    if (type === 'service_order') return `OS · ${SERVICE_ORDER_STATUS_LABELS[status] ?? status}`;
    if (type === 'contract') return `Contrato · ${CONTRACT_STATUS_LABELS[status] ?? status}`;
    if (type === 'receipt') {
      return `Recibo · ${RECEIPT_STATE_LABELS[status as keyof typeof RECEIPT_STATE_LABELS] ?? status}`;
    }
  }
  if (tab === 'payments') {
    return PAYMENT_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;
  }
  return QUOTE_STATUS_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function ReportFiltersSheet({
  tab,
  filters,
  onFiltersChange,
  clients,
  products,
  sortOptions,
  sortField,
  sortDirection,
  onSortFieldChange,
  onSortDirectionChange,
}: ReportFiltersSheetProps) {
  const applies = REPORT_FILTER_APPLICABILITY[tab];
  const active = activeFiltersForTab(filters, tab);
  const count = activeFilterCount(filters, tab);
  const client = clients.find((option) => option.id === active.clientId);
  const product = products.find((option) => option.id === active.productId);
  const productOptions = tab === 'stock'
    ? products.filter((option) => option.type === 'product' && option.trackStock)
    : products;

  const setStatus = (value: string) => {
    onFiltersChange({
      ...filters,
      statusByTab: { ...filters.statusByTab, [tab]: value === ALL ? null : value },
    });
  };

  const statusOptions = tab === 'items'
    ? QUOTE_STATUS_OPTIONS.filter((option) =>
        (SOLD_QUOTE_STATUSES as readonly string[]).includes(option.value))
    : QUOTE_STATUS_OPTIONS;

  return (
    <>
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            className="h-10 shrink-0 gap-2 rounded-full px-3"
            data-report-filter-trigger
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtros
            {count > 0 && (
              <Badge className="h-5 min-w-5 justify-center px-1.5 text-[10px]">{count}</Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[88dvh] overflow-y-auto rounded-t-2xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="text-left">
            <SheetTitle>Filtros e classificação</SheetTitle>
            <SheetDescription>
              O recorte e a ordem valem para a tela, o CSV e o XLSX.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            {applies.client && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Cliente</label>
                <Select
                  value={active.clientId ?? ALL}
                  onValueChange={(value) =>
                    onFiltersChange({ ...filters, clientId: value === ALL ? null : value })}
                >
                  <SelectTrigger><SelectValue placeholder="Todos os clientes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os clientes</SelectItem>
                    {clients.map((option) => (
                      <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {applies.product && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Produto</label>
                <Select
                  value={active.productId ?? ALL}
                  onValueChange={(value) =>
                    onFiltersChange({ ...filters, productId: value === ALL ? null : value })}
                >
                  <SelectTrigger><SelectValue placeholder="Todos os produtos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os produtos</SelectItem>
                    {productOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {reportProductLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {applies.status && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {tab === 'documents' ? 'Status por tipo de documento' : 'Status'}
                </label>
                <Select value={active.status ?? ALL} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue placeholder="Todos os status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os status</SelectItem>
                    {tab === 'documents' ? (
                      <>
                        <SelectGroup>
                          <SelectLabel>Ordens de serviço</SelectLabel>
                          {Object.entries(SERVICE_ORDER_STATUS_LABELS).map(([value, label]) => (
                            <SelectItem
                              key={`service_order:${value}`}
                              value={documentStatusToken({ type: 'service_order', status: value })}
                            >
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Contratos</SelectLabel>
                          {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
                            <SelectItem
                              key={`contract:${value}`}
                              value={documentStatusToken({ type: 'contract', status: value })}
                            >
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Recibos · estado derivado</SelectLabel>
                          {Object.entries(RECEIPT_STATE_LABELS).map(([value, label]) => (
                            <SelectItem
                              key={`receipt:${value}`}
                              value={documentStatusToken({ type: 'receipt', status: value })}
                            >
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </>
                    ) : tab === 'payments' ? (
                      PAYMENT_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))
                    ) : (
                      statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2 border-t pt-5">
              <label className="text-sm font-medium">Classificação</label>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Select value={sortField} onValueChange={onSortFieldChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 w-11 px-0"
                  aria-label={sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
                  title={sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
                  onClick={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')}
                >
                  {sortDirection === 'asc'
                    ? <ArrowUpAZ className="h-4 w-4" />
                    : <ArrowDownAZ className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {sortDirection === 'asc' ? 'Crescente' : 'Decrescente'}
              </p>
            </div>

            {count > 0 && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onFiltersChange(clearFiltersForTab(filters, tab))}
              >
                <X className="mr-2 h-4 w-4" />
                Limpar filtros
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {count > 0 && (
        <div
          className="flex min-w-0 flex-wrap items-center gap-1.5"
          data-report-filter-summary
        >
          {client && (
            <Badge variant="secondary" className="max-w-[128px]">
              <span className="truncate">Cliente: {client.name}</span>
            </Badge>
          )}
          {product && (
            <Badge variant="secondary" className="max-w-[128px]">
              <span className="truncate">Produto: {reportProductLabel(product)}</span>
            </Badge>
          )}
          {active.status && (
            <Badge variant="secondary" className="max-w-[140px]">
              <span className="truncate">{statusLabel(tab, active.status)}</span>
            </Badge>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onFiltersChange(clearFiltersForTab(filters, tab))}
          >
            Limpar filtros
          </Button>
        </div>
      )}
    </>
  );
}
